import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCongridUsers } from "@/hooks/useCongridUsers";
import { useUpdateUser } from "@/hooks/useUpdateUser";
import { useAuth } from "@/contexts/AuthContext";
import type { User, UserAccessLevel } from "@/types";

interface Props {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function AccessLevelBadgeInline({ level }: { level: UserAccessLevel }) {
  if (level === 'ADMIN') return <Badge variant="destructive">Ylläpitäjä</Badge>;
  if (level === 'INTERNAL_PLUS') return <Badge variant="default" className="bg-indigo-600 hover:bg-indigo-600">Sisäinen+</Badge>;
  if (level === 'INTERNAL') return <Badge variant="default" className="bg-blue-600 hover:bg-blue-600">Sisäinen</Badge>;
  return <Badge variant="secondary">Vieras</Badge>;
}

export function UserEditDialog({ user, open, onOpenChange }: Props) {
  const [congridUserId, setCongridUserId] = useState<string | null>(null);
  const [pendingAccessLevel, setPendingAccessLevel] = useState<UserAccessLevel | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { dbUser: currentDbUser } = useAuth();
  const { congridUsers, loading: loadingCongrid } = useCongridUsers(!open);
  const { updateUser, loading: saving } = useUpdateUser();

  useEffect(() => {
    if (open) {
      setCongridUserId(user?.congridUserId ?? null);
      setPendingAccessLevel(null);
      setSubmitError(null);
    }
  }, [open, user?.id, user?.congridUserId]);

  const options: ComboboxOption[] = useMemo(
    () =>
      congridUsers.map((u) => {
        const name = [u.firstName, u.lastName].filter(Boolean).join(" ");
        return {
          value: u.id,
          label: name || u.email || u.id,
          description: u.email ?? undefined,
        };
      }),
    [congridUsers],
  );

  const isCurrentAdmin = currentDbUser?.accessLevel === 'ADMIN';
  const isCurrentInternalPlus = currentDbUser?.accessLevel === 'INTERNAL_PLUS';
  const isCurrentInternal = currentDbUser?.accessLevel === 'INTERNAL';
  const targetLevel = user?.accessLevel;

  const handleSave = async () => {
    if (!user) return;
    setSubmitError(null);
    try {
      const result = await updateUser({
        id: user.id,
        congridUserId: congridUserId ?? "",
        accessLevel: pendingAccessLevel ?? undefined,
      });
      if (!result?.success) {
        setSubmitError(result?.message ?? "Tallennus epäonnistui");
        return;
      }
      onOpenChange(false);
    } catch (e: unknown) {
      setSubmitError(e instanceof Error ? e.message : "Tallennus epäonnistui");
    }
  };

  const renderAccessLevelSection = () => {
    if (!user || !targetLevel) return null;

    const isEditingSelf = currentDbUser?.id === user.id;
    const isDevelopmentAdmin = user.azureObjectId === "localhost-dev-admin";

    if (isEditingSelf) {
      return (
        <div className="space-y-2">
          <Label>Käyttöoikeus</Label>
          <div className="text-sm text-muted-foreground">
            Käyttäjät eivät voi muuttaa omia käyttöoikeuksiaan
          </div>
        </div>
      );
    }

    if (isDevelopmentAdmin) {
      return (
        <div className="space-y-2">
          <Label>Käyttöoikeus</Label>
          <div className="text-sm text-muted-foreground">
            Kehityksen pääkäyttäjää ei voi alentaa
          </div>
        </div>
      );
    }

    const currentEffective = pendingAccessLevel ?? targetLevel;

    const labels: Record<UserAccessLevel, string> = {
      GUEST: 'Vieras',
      INTERNAL: 'Sisäinen',
      INTERNAL_PLUS: 'Sisäinen+',
      ADMIN: 'Ylläpitäjä',
    };

    const levels: UserAccessLevel[] = ['GUEST', 'INTERNAL', 'INTERNAL_PLUS', 'ADMIN'];

    // Mirrors the backend access-level transition matrix:
    // - ADMIN: may set any target to any level.
    // - INTERNAL_PLUS / INTERNAL: may only modify targets that are currently
    //   GUEST or INTERNAL, and may only set them to GUEST or INTERNAL. Neither
    //   may grant ADMIN or INTERNAL_PLUS, and neither may touch ADMIN or
    //   INTERNAL_PLUS targets (only ADMIN can grant/revoke INTERNAL_PLUS).
    // - GUEST (or anything else): no changes allowed.
    const getDisabledLevels = (): UserAccessLevel[] => {
      if (isCurrentAdmin) return [];

      if (isCurrentInternalPlus || isCurrentInternal) {
        if (targetLevel === 'ADMIN' || targetLevel === 'INTERNAL_PLUS') {
          return levels;
        }
        return ['INTERNAL_PLUS', 'ADMIN'];
      }

      return levels;
    };

    const disabledLevels = getDisabledLevels();
    const isSelectDisabled = disabledLevels.length === levels.length;

    return (
      <div className="space-y-2">
        <Label>Käyttöoikeus</Label>
        <Select value={currentEffective} onValueChange={(val) => setPendingAccessLevel(val as UserAccessLevel)} disabled={isSelectDisabled}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {levels.map((lvl) => (
              <SelectItem key={lvl} value={lvl} disabled={disabledLevels.includes(lvl)}>
                {labels[lvl]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Muokkaa käyttäjää</DialogTitle>
          <DialogDescription>
            {user
              ? [user.firstName, user.lastName].filter(Boolean).join(" ") ||
                user.email
              : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {renderAccessLevelSection()}

          <div className="space-y-2">
            <Label>Congrid-käyttäjä</Label>
            <Combobox
              options={options}
              value={congridUserId}
              onChange={setCongridUserId}
              placeholder="Valitse Congrid-käyttäjä"
              searchPlaceholder="Hae..."
              loading={loadingCongrid}
            />
            <p className="text-xs text-muted-foreground">
              Linkittää tämän käyttäjän Congrid-alustan käyttäjään. Tarvitaan
              vastuuhenkilönä toimimiseen työvaiheissa ja tarkastuksissa.
            </p>
          </div>

          {submitError && (
            <p className="text-sm text-destructive">{submitError}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Peruuta
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Tallennetaan..." : "Tallenna"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
